import axios from 'axios';

// Set per-environment in .env.development / .env.production (picked up automatically
// by `npm start` / `npm run build`). Falls back to local dev if unset.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8089/api';

const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Attach admin token automatically when available
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
        config.headers = config.headers || {};
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
});

export const bookingAPI = {
    getAllBookings: () => api.get('/bookings'),
    getBookingById: (id) => api.get(`/bookings/${id}`),
    createBooking: (bookingData) => api.post('/bookings', bookingData),
    getAvailableSlots: (date, courtNumber) => 
        api.get(`/bookings/available-slots?date=${date}&courtNumber=${courtNumber}`),
    exportToExcel: () => api.get('/bookings/export', { responseType: 'blob' }),
    updateBooking: (id, bookingData) => api.put(`/bookings/${id}`, bookingData),
    updateBookingStatus: (id, status) => api.put(`/bookings/${id}/status`, { status }),
    editBooking: (id, editData) => api.put(`/bookings/${id}/edit`, editData),
    cancelBooking: (id) => api.put(`/bookings/${id}/cancel`),
    cancelBookingByUser: (id) => api.put(`/bookings/${id}/cancel-by-user`),
    sendDueReminder: (id) => api.post(`/bookings/${id}/remind-due`),
};

export const inventoryAPI = {
    getItems: () => api.get('/inventory/items'),
    addItem: (item) => api.post('/inventory/items', item),
    updateItem: (id, item) => api.put(`/inventory/items/${id}`, item),
    deleteItem: (id) => api.delete(`/inventory/items/${id}`),
    sellItem: (id, quantity, buyerName, paymentMethod, amountPaid) =>
        api.post(`/inventory/items/${id}/sell`, { quantity, buyerName, paymentMethod, amountPaid }),
    getRecentSales: () => api.get('/inventory/sales'),
    deleteSale: (id) => api.delete(`/inventory/sales/${id}`),
};

export const adminAPI = {
    login: (credentials) => api.post('/admin/login', credentials),
    validateToken: (token) => api.post('/admin/validate', null, {
        headers: { 'Authorization': `Bearer ${token}` }
    }),
    setupInitialAdmin: () => api.post('/admin/setup'),
};

export const reportAPI = {
    getMonthlyReport: (year, month) => api.get(`/reports/monthly?year=${year}&month=${month}`),
    getWeeklyReport: (startDate) => api.get(`/reports/weekly?startDate=${startDate}`),
    getCustomReport: (startDate, endDate, courtNumber, status) => {
        let url = `/reports/custom?startDate=${startDate}&endDate=${endDate}`;
        if (courtNumber) url += `&courtNumber=${courtNumber}`;
        if (status) url += `&status=${status}`;
        return api.get(url);
    },
    exportReport: (reportData) => api.post('/reports/export', reportData, { responseType: 'blob' }),
    exportMonthlyReport: (year, month) => api.get(`/reports/export/monthly?year=${year}&month=${month}`, { responseType: 'blob' }),
    exportWeeklyReport: (startDate) => api.get(`/reports/export/weekly?startDate=${startDate}`, { responseType: 'blob' }),
    getAvailableYears: () => api.get('/reports/years'),
    getAvailableMonths: (year) => api.get(`/reports/months?year=${year}`),
    getBookingsWithDues: () => api.get('/reports/dues')
};

export const pricingRuleAPI = {
    list: () => api.get('/pricing-rules'),
    listActive: () => api.get('/pricing-rules/active'),
    create: (rule) => api.post('/pricing-rules', rule),
    update: (id, rule) => api.put(`/pricing-rules/${id}`, rule),
    remove: (id) => api.delete(`/pricing-rules/${id}`)
};

export const bulkAPI = {
    preview: (payload) => api.post('/bulk-bookings/preview', payload),
    apply: (payload) => api.post('/bulk-bookings/apply', payload)
};

export default api;
